import axios, { AxiosRequestConfig, AxiosResponse, Canceler } from 'axios';

import { getConfig } from '@constants';

import { Log, counters } from '@utils';

function isForeign(url: string) {
	const urlHost = url.includes('//') && url.substring(url.indexOf('//'), url.indexOf('/', 9));
	const { hosts: { api: apiHost } } = getConfig();

	return urlHost &&
		urlHost !== location.host &&
		urlHost !== apiHost;
}

export class BrowserHttpTransport {

	static axios: typeof axios = axios;
	protected static sdcQueue: AxiosRequestConfig[] = [];
	// protected static sdcQueuePromise: Promise<unknown>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	protected static deferredBySdcRequests: Promise<AxiosResponse[]>;
	protected static fetchSdcPromise: Promise<void>;
	// protected static isNeedSdcCheck = true;
	protected static isSdcInProgress: boolean;

	static setup() {
		const { hosts: { api } } = getConfig();

		axios.defaults.baseURL = api;

		this.setUpSdcCookies();
		this.setUnregHeaders();
		this.setAuthorizationHeader();
		this.setRefererHeader();
	}

	static async http(params: AxiosRequestConfig & { getAbort?(abort: Canceler): void }) {
		return axios({
			// eslint-disable-next-line import/no-named-as-default-member
			cancelToken: new axios.CancelToken((abort: Canceler) => params.getAbort && params.getAbort(abort)),
			...params,
		});
	}

	private static setUpSdcCookies() {
		// на опенстаке и локалке не удаётся запросить sdc c auth.mail.ru
		if (getConfig().isProcessingSdc) {
			axios.interceptors.response.use(
				async (response: AxiosResponse) => {
					const responseWithRelevantSdc = await this.checkSdcCookies(response);

					if (responseWithRelevantSdc) {
						return responseWithRelevantSdc;
					}
				},
			);
		}
	}

	static setUnregHeaders() {
		axios.interceptors.request.use((config: AxiosRequestConfig) => {
			if (isForeign(config.url)) {
				return config;
			}

			const headers = { ...config.headers };

			return {
				...config,
				headers: {
					...headers,
				},
			};
		});
	}

	static setAuthorizationHeader() {
		axios.interceptors.request.use((config: AxiosRequestConfig) => {
			if (isForeign(config.url)) {
				return config;
			}

			const headers = { ...config.headers };

			return {
				...config,
				headers: {
					...headers,
					['Authorization']: `Bearer `,
				},
			};
		});
	}

	static setRefererHeader() {
		axios.interceptors.request.use((config: AxiosRequestConfig) => {
			if (isForeign(config.url)) {
				return config;
			}

			return {
				...config,
				headers: {
					...config.headers,
				},
			};
		});
	}

	protected static async fetchSdc(url: string) {
		return new Promise<void>((resolve, reject) => {
			const script = document.createElement('script');

			script.src = url + encodeURIComponent(getConfig().hosts.baseUrl) + '&JSONP_call=1';
			script.onload = () => resolve();
			script.onerror = () => reject(new Error(`Error while loading sdc script ${script.src}`));

			document.head.append(script);
		});
	}

	protected static async checkSdcCookies(response: AxiosResponse) {
		const {
			data,
			config: params,
		} = response;
		let correctResponse = null;
		const requestIndex = this.sdcQueue.length;
		const [, errorType, url] = Array.isArray(data) ? data : [];
		// получаем ответ из массива запросов, которые отсрочились из-за получения куки
		const getDeferredResponseFromQueue = async () => {
			try {
				return (await this.deferredBySdcRequests)?.[requestIndex] || null;
			} catch (err) {
				Log.error(`Can't get data from sdcQueuePromiseArray: ${err}`);
			}

			return null;
		};

		if (errorType === 'Auth' || errorType === 'NoSDC') {
			counters.sendGrStat('monitoring3.sdc-spam.noname');
			this.sdcQueue.push(params);

			if (!this.isSdcInProgress) {
				this.isSdcInProgress = true;

				// запрашиваем sdc куку
				try {
					this.fetchSdcPromise = this.fetchSdc(url as string);
					await this.fetchSdcPromise;
				} catch (err) {
					counters.sendGrStat('monitoring3.sdc-error');
					Log.error(`Couldn't get SDK cookie: ${err}`);
				}

				try {
					// создаём массив промисов для запросов
					// которые слегли с ошибкой из-за sdc
					this.deferredBySdcRequests = Promise.all(
						this.sdcQueue.map( async (params) =>
							await BrowserHttpTransport.http(params),
						),
					);

					correctResponse = await getDeferredResponseFromQueue();
					// очищаем массив конфигов запросов, которые сломались из-за sdc куки
					this.sdcQueue = [];
				} catch (err) {
					Log.error(`Couldn't get requests while checking sdc in BrowserHttpTransport: ${err}`);
				}

				this.isSdcInProgress = false;
			} else {
				// ожидаем, пока дойдёт SDC кука
				// и вытаскиваем ответы из очереди отсроченных промисов
				await this.fetchSdcPromise;

				correctResponse = await getDeferredResponseFromQueue();
			}
		} else {
			// sdc кука есть - шлём изначальный ответ
			correctResponse = response;
		}

		return correctResponse;
	}

}
